import { useEffect, useMemo, useState } from 'react';
import { differenceInCalendarDays, endOfDay, format, startOfDay, subDays } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { UsageCredits } from 'features/usage/components/usage-credits';
import { UsageDashboard } from 'features/usage/components/usage-dashboard';
import { UsageHeader } from 'features/usage/components/usage-header';
import { UsageLoadingSkeleton } from 'features/usage/components/usage-loading-skeleton';
import { UsageRangeWarning } from 'features/usage/components/usage-range-warning';
import { UsageScopeTabs } from 'features/usage/components/usage-scope-tabs';
import { UsageStateCard } from 'features/usage/components/usage-state-card';
import {
  useSubscriptionUsage,
  useTokenUsage,
  useTokenUsageByOperation,
} from 'features/usage/hooks/use-usage';
import { TokenUsageScope } from 'features/usage/services/usage-service';
import { buildUsageViewModel } from 'features/usage/utils/build-usage-view-model';
import { getErrorMessage } from 'features/usage/utils/usage-format';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { useCommonStore } from 'state/store/common/useCommonStore';

const MAX_RANGE_DAYS = 365;

export default function UsagePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUserId = useCommonStore((state) => state.user?.id) ?? '';
  const canViewOrgUsage = isAdmin || isOrgAdmin;
  const requestedUserId = searchParams.get('userId') ?? '';
  const viewingUserId = canViewOrgUsage ? requestedUserId : '';
  const viewingUserName = (location.state as { userName?: string } | null)?.userName;
  const today = useMemo(() => new Date(), []);
  const initialRange = useMemo<DateRange>(
    () => ({
      from: subDays(today, 6),
      to: today,
    }),
    [today]
  );

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialRange);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [tab, setTab] = useState<'personal' | 'organization'>('personal');
  const showTabs = canViewOrgUsage && !viewingUserId;
  const scope: TokenUsageScope = viewingUserId
    ? 'user'
    : showTabs && tab === 'organization'
      ? 'org'
      : 'me';

  useEffect(() => {
    if (!isWarningVisible) return undefined;

    const timeoutId = window.setTimeout(() => {
      setIsWarningVisible(false);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [isWarningVisible]);

  // Which subscription meter the credits card reports: the organisation's own row
  // (empty userId) on the org tab, otherwise the row for whoever is being viewed.
  const meterUserId = scope === 'org' ? '' : viewingUserId || currentUserId;

  const rangeStart = startOfDay(selectedRange?.from ?? initialRange.from ?? today);
  const rangeEnd = endOfDay(selectedRange?.to ?? selectedRange?.from ?? initialRange.to ?? today);
  const startDate = format(rangeStart, 'yyyy-MM-dd');
  const endDate = format(rangeEnd, 'yyyy-MM-dd');

  const usageParams = {
    startDate,
    endDate,
    scope,
    userId: viewingUserId || undefined,
  };
  const rangeEnabled = !!selectedRange?.from;
  const usageQuery = useTokenUsage(usageParams, rangeEnabled);
  const operationQuery = useTokenUsageByOperation(usageParams, rangeEnabled);
  const subscriptionUsageQuery = useSubscriptionUsage();

  const usageData = usageQuery.data;
  const viewModel = useMemo(
    () => (usageData ? buildUsageViewModel(usageData) : null),
    [usageData]
  );

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (range?.from && range.to) {
      const days = differenceInCalendarDays(range.to, range.from) + 1;
      if (days > MAX_RANGE_DAYS) {
        setIsWarningVisible(true);
        return;
      }
    }
    setSelectedRange(range);
  };

  return (
    <ConfigurationLayout>
      <UsageHeader
        periodStart={usageData?.period.start}
        periodEnd={usageData?.period.end}
        selectedRange={selectedRange}
        initialFrom={initialRange.from}
        today={today}
        viewingUserId={viewingUserId || undefined}
        viewingUserName={viewingUserName}
        dayCount={viewModel?.periodDays.length ?? 0}
        onRangeSelect={handleRangeSelect}
        onResetRange={() => setSelectedRange(initialRange)}
        onBack={() => navigate('/identity-management')}
      />

      {showTabs && <UsageScopeTabs tab={tab} onTabChange={setTab} />}

      <UsageCredits meters={subscriptionUsageQuery.data ?? []} userId={meterUserId} />
      {isWarningVisible && <UsageRangeWarning onDismiss={() => setIsWarningVisible(false)} />}
      {usageQuery.isLoading && <UsageLoadingSkeleton />}

      {usageQuery.isError && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('DATA_UNAVAILABLE')}
          description={getErrorMessage(usageQuery.error, t('LOAD_FAILED'))}
        />
      )}

      {!usageQuery.isLoading && !usageQuery.isError && !usageData && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('NO_DATA')}
          description={t('NO_DATA_FOR_RANGE')}
        />
      )}

      {!usageQuery.isLoading && !usageQuery.isError && usageData && viewModel && (
        <UsageDashboard
          data={usageData}
          viewModel={viewModel}
          operations={operationQuery.data ?? []}
          showUsers={scope === 'org'}
        />
      )}
    </ConfigurationLayout>
  );
}
